import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/native';
import { SccButton } from '@/components/atoms';
import { SccPressable } from '@/components/SccPressable';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import BottomSheet from '@/modals/BottomSheet/BottomSheet';
import { THEME_OPTIONS } from '../constants';
/**
 * 관심 주제(테마) 선택 바텀시트.
 *
 * Figma 1629:29991 디자인 기준:
 * - 2-column grid 8개. 각 칩은 이모지 + 라벨.
 * - 선택 시 brand 색 border, 선택 안 됨은 gray border.
 * - 하단 "확인" 버튼: 선택된 테마 1개 이상일 때 활성.
 */
export default function ThemeSelectBottomSheet({ isVisible, initialSelectedThemes, onConfirm, onClose, }) {
    const [selectedThemes, setSelectedThemes] = useState(initialSelectedThemes);
    useEffect(() => {
        if (isVisible) {
            setSelectedThemes(initialSelectedThemes);
        }
    }, [isVisible, initialSelectedThemes]);
    const toggleTheme = useCallback((theme) => {
        setSelectedThemes(prev => prev.includes(theme) ? prev.filter(p => p !== theme) : [...prev, theme]);
    }, []);
    const handleConfirm = useCallback(() => {
        onConfirm(selectedThemes);
    }, [selectedThemes, onConfirm]);
    const isConfirmEnabled = selectedThemes.length > 0;
    return (_jsx(BottomSheet, { isVisible: isVisible, onPressBackground: onClose, children: _jsxs(LogParamsProvider, { params: { displaySectionName: 'interested_theme_bottom_sheet' }, children: [_jsx(SheetHeader, { children: _jsx(SheetHandle, {}) }), _jsxs(ContentsContainer, { children: [_jsxs(TitleArea, { children: [_jsx(Title, { children: "\uAD00\uC2EC \uC8FC\uC81C\uB97C \uBAA8\uB450 \uC120\uD0DD\uD574\uC8FC\uC138\uC694." }), _jsx(Description, { children: "\uB9DE\uCDA4 \uC815\uBCF4 \uC81C\uACF5 \uBC0F \uD0D0\uC0C9\uC744 \uC704\uD574 \uD544\uC694\uD55C \uC815\uBCF4\uC785\uB2C8\uB2E4." })] }), _jsx(ThemeGrid, { children: THEME_OPTIONS.map(theme => {
                                const isSelected = selectedThemes.includes(theme.value);
                                return (_jsx(ThemeChip, { elementName: "interested_theme_chip", logParams: { theme: theme.value }, onPress: () => toggleTheme(theme.value), selected: isSelected, children: _jsx(ThemeChipText, { selected: isSelected, children: theme.label }) }, theme.value));
                            }) })] }), _jsx(BottomBar, { children: _jsx(SccButton, { text: "\uD655\uC778", elementName: "interested_theme_confirm_button", onPress: handleConfirm, buttonColor: "brand40", textColor: "white", fontFamily: font.pretendardSemibold, fontSize: 18, height: 56, isDisabled: !isConfirmEnabled, style: { borderRadius: 8 } }) })] }) }));
}
const SheetHeader = styled.View `
  width: 100%;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding-bottom: 4px;
`;
const SheetHandle = styled.View `
  width: 48px;
  height: 4px;
  border-radius: 2px;
  background-color: ${color.gray20v2};
`;
const ContentsContainer = styled.View `
  padding: 0 20px 20px;
  background-color: ${color.white};
`;
const TitleArea = styled.View `
  gap: 4px;
`;
const Title = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 22px;
  line-height: 30px;
  letter-spacing: -0.44px;
  color: ${color.black};
`;
const Description = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.gray70v2};
`;
const ThemeGrid = styled.View `
  margin-top: 20px;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  row-gap: 12px;
`;
const ThemeChip = styled(SccPressable) `
  width: 48%;
  padding: 14px 8px;
  border-radius: 14px;
  border-width: 1.2px;
  border-color: ${({ selected }) => (selected ? color.brand40 : color.gray20v2)};
  background-color: ${({ selected }) => (selected ? color.brand10 : color.white)};
  align-items: center;
  justify-content: center;
`;
const ThemeChipText = styled.Text `
  font-family: ${({ selected }) => selected ? font.pretendardSemibold : font.pretendardMedium};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  text-align: center;
  color: ${({ selected }) => (selected ? color.brand50 : color.gray80v2)};
`;
const BottomBar = styled.View `
  padding: 16px 20px 24px;
  background-color: ${color.white};
`;
